import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Pager } from '@/components/pager';
import { Badge, Callout, Field, buttonClass, inputClass } from '@/components/ui';
import { ProductPicker } from '@/components/admin/stock-matching';
import { adminLocationsQuery, adminStockTakesQuery, meQuery, styleQuery } from '@/queries';
import type { StockTake, StockTakeLine, TakeState } from '@/lib/stock-types';

/**
 * Stocktakes. Spec 003 US-2, task T057, invariant S-5.
 *
 * The screen is deliberately plain about three things the database enforces,
 * because a rule the user cannot see is a rule they will fight:
 *
 * **Counting locks the shelf.** While a take is open, nothing can move off that
 * location. Otherwise the variance measures how long the count took.
 *
 * **Blank is not zero.** A line nobody entered and a line counted at zero mean
 * opposite things, so approval is refused until every line is answered.
 *
 * **An admin approves.** FR-110. A manager cannot, which may be wrong for this
 * client (Q-16), so the button says WHY rather than sitting there greyed out
 * with no explanation.
 */

const STATE_LABEL: Record<TakeState, string> = {
  open: 'Counting',
  counted: 'Awaiting approval',
  approved: 'Approved',
  cancelled: 'Cancelled',
};

const STATE_TONE: Record<TakeState, 'ok' | 'brand' | 'warn'> = {
  open: 'brand',
  counted: 'warn',
  approved: 'ok',
  cancelled: 'warn',
};

interface FoundItem {
  id: string;
  code: string;
  name: string;
  base_uom: string;
}

async function call(path: string, init?: RequestInit) {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : await res.json();
}

/**
 * Something on the shelf that the sheet does not list.
 *
 * The sheet is built from the ledger, so a new shelf opens empty and a
 * misplaced box never appears on it. Without this the counter could see the
 * stock in front of them and had nowhere to write it down. The count they
 * enter here goes on a line with an expected quantity of zero, and approval
 * turns the difference into stock on this shelf.
 */
function AddFoundItem({ takeId, onAdded }: { takeId: string; onAdded: () => void }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<FoundItem | null>(null);
  const [qty, setQty] = useState('');
  const [said, setSaid] = useState<string | null>(null);

  const search = useQuery({
    queryKey: ['admin', 'stock-takes', takeId, 'items', q.trim()],
    queryFn: async () =>
      (await call(`/api/stock-takes/${takeId}/items?q=${encodeURIComponent(q.trim())}`)) as
        { items: FoundItem[] },
    enabled: picked === null && q.trim().length >= 2,
    staleTime: 10_000,
  });

  const add = useMutation({
    mutationFn: () => call(`/api/stock-takes/${takeId}/lines`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ item_id: picked?.id, counted_qty: Number(qty) }),
    }),
    onSuccess: () => {
      setPicked(null);
      setQ('');
      setQty('');
      setSaid(null);
      onAdded();
    },
    onError: (e: Error) => setSaid(e.message),
  });

  function submit() {
    if (!picked) {
      setSaid('Choose the item first.');
      return;
    }
    const n = Number(qty);
    if (qty.trim() === '' || !Number.isFinite(n) || n < 0) {
      setSaid('Enter how many you counted. Zero or more.');
      return;
    }
    add.mutate();
  }

  const results = search.data?.items ?? [];

  return (
    <div className="mt-4 rounded-[12px] border border-line p-3">
      <p className="text-[14px] font-semibold">Add an item you found on this shelf</p>
      <p className="mt-0.5 text-[12.5px] text-muted">
        Use this for stock that is on the shelf but not on the sheet. Find it by
        code or name, then enter the count.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        {picked ? (
          <div className="flex h-9 min-w-[280px] items-center justify-between gap-3 rounded-[8px] border border-line bg-panel px-3 text-[14px]">
            <span>
              <span className="font-mono text-[13px] text-muted">{picked.code}</span>{' '}
              {picked.name}
            </span>
            <button
              type="button"
              className="text-[13px] text-muted underline"
              onClick={() => { setPicked(null); setSaid(null); }}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Item code or name"
              aria-label="Find an item by code or name"
              className={`${inputClass} w-[280px]`}
            />
            {q.trim().length >= 2 && (
              <div className="absolute left-0 top-full z-10 mt-1 max-h-[240px] w-[360px] max-w-[80vw] overflow-auto rounded-[10px] border border-line bg-panel shadow-sm">
                {search.isPending ? (
                  <p className="px-3 py-2 text-[13px] text-muted">Looking…</p>
                ) : search.isError ? (
                  <p className="px-3 py-2 text-[13px] text-muted">{(search.error as Error).message}</p>
                ) : results.length === 0 ? (
                  <p className="px-3 py-2 text-[13px] text-muted">
                    No item matches, or it is already on the sheet.
                  </p>
                ) : (
                  results.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => { setPicked(i); setSaid(null); }}
                      className="block w-full px-3 py-2 text-left text-[14px] hover:bg-raised"
                    >
                      <span className="font-mono text-[13px] text-muted">{i.code}</span>{' '}
                      {i.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        <input
          type="number"
          min={0}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          placeholder="Count"
          aria-label="How many you counted"
          className="h-9 w-24 rounded-[8px] border border-line bg-panel px-2 text-right"
        />
        {picked && <span className="pb-2 text-[13px] text-muted">{picked.base_uom}</span>}

        <button
          type="button"
          className={buttonClass('primary', 'sm')}
          disabled={add.isPending}
          onClick={submit}
        >
          {add.isPending ? 'Adding' : 'Add to the count'}
        </button>
      </div>

      {said && <p className="mt-2 text-[13px] text-muted">{said}</p>}
    </div>
  );
}

function TakeDetail({ take, isAdmin }: { take: StockTake; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [lines, setLines] = useState<StockTakeLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useMutation({
    mutationFn: () => call(`/api/stock-takes/${take.id}`),
    onSuccess: (d) => setLines((d as { lines: StockTakeLine[] }).lines),
    onError: (e: Error) => setError(e.message),
  });

  /**
   * Send one counted line.
   *
   * One place, called from both the blur and the Enter key, so the two cannot
   * drift apart. Skips the send when the value has not changed, which stops a
   * tab through an untouched sheet writing every line back.
   */
  function commit(lineId: string, raw: string): void {
    const text = raw.trim();
    const qty = text === '' ? null : Number(text);
    const current = (lines ?? []).find((l) => l.id === lineId)?.counted_qty;
    const currentNum = current == null ? null : Number(current);
    if (qty === currentNum) return;
    count.mutate({ id: lineId, qty });
  }

  const count = useMutation({
    mutationFn: ({ id, qty }: { id: string; qty: number | null }) =>
      call(`/api/stock-take-lines/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ counted_qty: qty }),
      }),
    onSuccess: () => load.mutate(),
    onError: (e: Error) => setError(e.message),
  });

  const act = useMutation({
    mutationFn: (what: 'submit' | 'approve' | 'cancel') =>
      call(`/api/stock-takes/${take.id}/${what}`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'stock-takes'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      load.mutate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const blanks = (lines ?? []).filter((l) => l.counted_qty == null).length;
  const withVariance = (lines ?? []).filter((l) => Number(l.variance ?? 0) !== 0);

  return (
    <div className="border-t border-line p-4">
      {lines === null ? (
        <button
          type="button"
          onClick={() => load.mutate()}
          className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
       >
          {load.isPending ? 'Loading the count…' : 'Open the count sheet'}
        </button>
      ) : (
        <>
          {error && (
            <p className="mb-3 rounded-[10px] border border-line bg-panel p-3 text-[14px]">
              {error}
            </p>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-3 text-[14px]">
            <span>{lines.length} lines</span>
            {lines.length === 0 ? (
              <Badge tone="warn">nothing on the sheet yet</Badge>
            ) : blanks> 0 ? (
              <Badge tone="warn">{blanks} not counted yet</Badge>
            ) : (
              <Badge tone="ok">every line counted</Badge>
            )}
            {withVariance.length> 0 && (
              <span className="text-muted">
                {withVariance.length} with a variance
              </span>
            )}
          </div>

          <div className="max-h-[46vh] overflow-auto rounded-[12px] border border-line">
            <table className="dt w-full">
              <thead className="sticky top-0 bg-panel">
                {/* Spec 014, FR-1440. Product, colour and size in their own
                    columns, in the order the server sorts them (FR-1442), so
                    the sheet reads the way the shelf is laid out. */}
                <tr className="border-b border-line text-left text-[12px] uppercase tracking-wide text-muted">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Colour</th>
                <th className="px-3 py-2">Size</th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2 num">Expected</th>
                <th className="px-3 py-2 num">Counted</th>
                <th className="px-3 py-2 num">Variance</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-[14px] text-muted">
                      Nothing is recorded on this shelf, so there is nothing to
                      expect. {take.state === 'open'
                        ? 'Add each item you find below, with how many there are.'
                        : ''}
                    </td>
                  </tr>
                )}
                {lines.map((l) => {
                  const v = l.counted_qty == null ? null : Number(l.variance ?? 0);
                  return (
                    <tr key={l.id} className="border-b border-line last:border-0">
                <td className="px-3 py-2">
                        {l.items?.product_variants?.products?.name ?? l.items?.name}
                      </td>
                <td className="px-3 py-2">{l.items?.product_variants?.colour ?? ''}</td>
                <td className="px-3 py-2">{l.items?.product_variants?.size ?? ''}</td>
                <td className="px-3 py-2 font-mono text-[13px] text-muted">{l.items?.code}</td>
                <td className="px-3 py-2 num text-muted">
                        {Number(l.expected_qty)} {l.items?.base_uom}
                      </td>
                <td className="px-3 py-2 num">
                        <input
                          type="number"
                          defaultValue={l.counted_qty ?? ''}
                          disabled={take.state !== 'open'}
                          aria-label={`Counted quantity for ${l.items?.code}`}
                          placeholder="not counted"
                          onBlur={(e) => commit(l.id, e.target.value)}
                          // Enter commits too. Somebody counting a shelf types a
                          // number and presses Enter; they do not tab out of the
                          // last field, and until this existed that last count was
                          // never sent.
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commit(l.id, (e.target as HTMLInputElement).value);
                            }
                          }}
                          className="h-8 w-24 rounded-[8px] border border-line bg-panel px-2 text-right"
                        />
                      </td>
                <td className="px-3 py-2 num">
                        {v == null ? (
                          <span className="text-muted">—</span>
                        ) : v === 0 ? (
                          <span className="text-muted">0</span>
                        ) : (
                          <Badge tone="warn">{v> 0 ? `+${v}` : v}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {take.state === 'open' && (
            <AddFoundItem
              takeId={take.id}
              onAdded={() => { setError(null); load.mutate(); }}
            />
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            {take.state === 'open' && (
              <button
                type="button"
                // NOT disabled on `blanks`, which is what made this button dead
                // and silent. A count is typed into the last box and the operator
                // clicks straight here: the click blurs the box, which saves it,
                // but the click itself lands on a disabled button and does
                // nothing, and says nothing. They read that as broken. R-190.
                disabled={act.isPending}
                onClick={() => {
                  // The blur fired on the way to this click, so the save may still
                  // be in flight. Waiting for it means the first click works.
                  if (count.isPending) {
                    setError('Saving the last count. Press it again in a moment.');
                    return;
                  }
                  if (blanks > 0) {
                    setError(
                      `${blanks} ${blanks === 1 ? 'line has' : 'lines have'} no count yet. `
                        + 'Every line needs a number before this can be handed over.',
                    );
                    return;
                  }
                  setError(null);
                  act.mutate('submit');
                }}
                className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
             >
                Hand over for approval
              </button>
            )}

            {take.state === 'counted' && (
              <>
                <button
                  type="button"
                  disabled={!isAdmin || act.isPending}
                  onClick={() => act.mutate('approve')}
                  className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
               >
                  Approve and adjust stock
                </button>
                {!isAdmin && (
                  // Q-16. Say why. A greyed button with no reason is how a
                  // person concludes the software is broken.
                  <span className="text-[13px] text-muted">
                    Only an admin can approve a stocktake, so the adjustment is
                    signed off by someone who carries the cost of it. If that is
                    wrong for how you work, say so and it is a one line change.
                  </span>
                )}
              </>
            )}

            {(take.state === 'open' || take.state === 'counted') && (
              <button
                type="button"
                onClick={() => act.mutate('cancel')}
                className="rounded-[10px] border border-line px-3 py-2 text-[14px] text-muted"
             >
                Cancel this count
              </button>
            )}

            {take.state === 'approved' && (
              <p className="text-[14px] text-muted">
                Approved. One movement was written for each line that did not
                match, and the shelf is unlocked.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}


/**
 * Starting one, from the screen where you would look for it. R-274.
 *
 * The control existed and lived on the LOCATIONS screen, because a stocktake is
 * opened against a bay. That is true and it is not where anybody goes looking:
 * Christian opened Stocktakes, found four buttons that all act on a take that
 * already exists, and asked how you create one. A feature nobody can find is a
 * feature nobody has.
 *
 * So it is here as well, on the same route, against the same function. The one
 * on Locations stays, because opening a take while looking at the bay you are
 * about to count is also a sensible moment.
 */
function StartStockTake() {
  const qc = useQueryClient();
  const [locationId, setLocationId] = useState('');
  const [said, setSaid] = useState<string | null>(null);
  // Spec 014, FR-1441. A count of one product, or of one whole location.
  const [by, setBy] = useState<'location' | 'product'>('location');

  const { locations } = useSuspenseQuery(adminLocationsQuery).data as unknown as {
    locations: Array<{ id: string; code: string; label: string; zone: string }>;
  };

  const open = useMutation({
    mutationFn: () => call('/api/stock-takes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ location_id: locationId }),
    }),
    onSuccess: () => {
      setSaid(null);
      setLocationId('');
      void qc.invalidateQueries({ queryKey: ['admin', 'stock-takes'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  if (locations.length === 0) {
    return (
      <Callout tone="warn" title="There is nowhere to count yet">
        A stocktake is opened against one bay, so add a location first. Locations
        is in the menu, under Stock control.
      </Callout>
    );
  }

  return (
    <div className="mb-5 rounded-[10px] border border-line bg-panel p-4">
      <div className="mb-3 flex flex-wrap gap-1" role="group" aria-label="What to count">
        {(['location', 'product'] as const).map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={by === k}
            className={buttonClass(by === k ? 'primary' : 'secondary', 'sm')}
            onClick={() => { setBy(k); setSaid(null); }}
          >
            {k === 'location' ? 'Count a location' : 'Count a product'}
          </button>
        ))}
      </div>

      {by === 'product' ? <StartProductCount locations={locations} /> : (
      <>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Count a location" htmlFor="st-loc" hint="One bay at a time">
          <select
            id="st-loc"
            className={`${inputClass} w-[280px]`}
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            <option value="">Choose a location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.zone} · {l.code} {l.label}</option>
            ))}
          </select>
        </Field>
        <button
          type="button"
          className={buttonClass('primary', 'sm')}
          disabled={!locationId || open.isPending}
          onClick={() => { setSaid(null); open.mutate(); }}
        >
          {open.isPending ? 'Opening' : 'Start a stocktake'}
        </button>
      </div>

      {said && <Callout tone="danger" title={said} className="mt-3" />}

      {/* The lock is the part people are surprised by, so it is said before
          they start rather than after they are stopped. */}
      <p className="mt-3 text-[12.5px] text-muted">
        While a count is open, that location is locked: stock cannot move in or out
        of it until the count is approved or cancelled. Count one bay at a time.
      </p>
      </>
      )}
    </div>
  );
}

/**
 * A count by product. Spec 014, FR-1441, Part 11.4 of the meeting.
 *
 * Choose the product, then the locations. The locations that hold it are
 * ticked to start with. Each location gets its own count, with every stock
 * item of the product on it, so a size the ledger says is not there is on the
 * sheet as zero and a box of it found on the shelf has a line to go on.
 */
function StartProductCount(
  { locations }: { locations: Array<{ id: string; code: string; label: string; zone: string }> },
) {
  const qc = useQueryClient();
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [extra, setExtra] = useState('');
  const [said, setSaid] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const style = useQuery({ ...styleQuery(product?.id ?? ''), enabled: Boolean(product), retry: false });
  const idOf = new Map(locations.map((l) => [l.code, l.id]));
  // Where the ledger says the product is, until the person changes the list.
  const held = [...new Set((style.data?.stock ?? []).flatMap((s) => s.places.map((p) => idOf.get(p.location))))]
    .filter((x): x is string => Boolean(x));
  const ticked = chosen ?? held;
  const shown = [...new Set([...held, ...ticked])];
  const codeOf = new Map(locations.map((l) => [l.id, l]));

  const open = useMutation({
    mutationFn: () => call('/api/stock-takes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ product_id: product!.id, location_ids: ticked }),
    }),
    onSuccess: (d) => {
      const n = ((d as { take_ids?: string[] }).take_ids ?? []).length;
      setSaid(null);
      setDone(`${n} ${n === 1 ? 'count is' : 'counts are'} open for ${product!.name}. Open each one below.`);
      setProduct(null);
      setChosen(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'stock-takes'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  return (
    <div>
      <div className="max-w-[420px]">
        <Field label="Product" htmlFor="st-product">
          <ProductPicker
            id="st-product"
            value={product as never}
            onPick={(p) => { setProduct(p); setChosen(null); setDone(null); setSaid(null); }}
          />
        </Field>
      </div>

      {product && (
        <fieldset className="mt-3">
          <legend className="text-[13px] font-semibold">Locations to count</legend>
          {style.isLoading && <p className="text-[13px] text-muted">Finding where it is</p>}
          {style.data && held.length === 0 && (
            <p className="text-[13px] text-muted">The ledger has none of it on a shelf. Add the locations to count.</p>
          )}
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[13.5px]">
            {shown.map((id) => (
              <li key={id}>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={ticked.includes(id)}
                    onChange={(e) => setChosen(e.target.checked
                      ? [...ticked, id]
                      : ticked.filter((x) => x !== id))}
                  />
                  {codeOf.get(id)?.code ?? id}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <select
              aria-label="Add a location"
              className={`${inputClass} w-[240px]`}
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
            >
              <option value="">Add a location</option>
              {locations.filter((l) => !shown.includes(l.id)).map((l) => (
                <option key={l.id} value={l.id}>{l.zone} · {l.code} {l.label}</option>
              ))}
            </select>
            <button
              type="button"
              className={buttonClass('secondary', 'sm')}
              disabled={!extra}
              onClick={() => { setChosen([...ticked, extra]); setExtra(''); }}
            >
              Add
            </button>
          </div>
        </fieldset>
      )}

      <button
        type="button"
        className={`${buttonClass('primary', 'sm')} mt-3`}
        disabled={!product || ticked.length === 0 || open.isPending}
        onClick={() => { setSaid(null); open.mutate(); }}
      >
        {open.isPending ? 'Opening' : `Start ${ticked.length > 1 ? `${ticked.length} counts` : 'the count'}`}
      </button>

      {said && <Callout tone="danger" title={said} className="mt-3" />}
      {done && <Callout tone="ok" title={done} className="mt-3" />}

      <p className="mt-3 text-[12.5px] text-muted">
        Each location gets its own count, with every stock item of the product on it. While a
        count is open, its location is locked: stock cannot move in or out of it until the count
        is approved or cancelled.
      </p>
    </div>
  );
}

export default function AdminStockTakePage() {
  const [page, setPage] = useState(1);
  const takePage = useSuspenseQuery(adminStockTakesQuery(page)).data;
  const takes = takePage.rows;
  // `isAdmin` comes from the server's own answer, not from a role string read
  // in the browser. The button it controls is a courtesy either way: the
  // database refuses an unauthorised approval on its own (FR-110).
  const isAdmin = useSuspenseQuery(meQuery).data.isAdmin;
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Stocktakes</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Count a shelf, see what does not match, and adjust the stock once
          somebody has signed it off.
        </p>
      </div>

      <StartStockTake />

      <div className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px] text-muted">
        While a count is open, <strong>nothing can move off that location</strong>.
        That is deliberate: if stock leaves the shelf mid-count, the variance
        measures how long the count took rather than what is on the shelf.
      </div>

      {takes.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">No stocktakes yet.</p>
          <p className="mx-auto mt-2 max-w-[46ch] text-[14px] text-muted">
            A count starts from a location, once locations and stock exist.
          </p>
        </div>
      ) : (
        <div className="rounded-[10px] border border-line bg-panel">
          {takes.map((take) => (
            <div key={take.id}>
              <button
                type="button"
                onClick={() => setOpenId(openId === take.id ? null : take.id)}
                className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
             >
                <span className="flex items-center gap-3">
                  <span className="font-semibold">
                    {take.locations?.code ?? 'Location'}
                  </span>
                  <span className="text-[14px] text-muted">
                    {take.locations?.label}
                  </span>
                  {take.products?.name && (
                    <span className="text-[14px]">Count of {take.products.name}</span>
                  )}
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-[13px] text-muted">
                    {new Date(take.opened_at).toLocaleDateString('en-AU')}
                  </span>
                  <Badge tone={STATE_TONE[take.state]}>{STATE_LABEL[take.state]}</Badge>
                </span>
              </button>
              {openId === take.id && <TakeDetail take={take} isAdmin={isAdmin} />}
            </div>
          ))}
          <Pager
            page={takePage.page}
            pages={takePage.pages}
            total={takePage.total}
            limit={takePage.limit}
            noun="stocktake"
            onPage={(n) => { setPage(n); window.scrollTo({ top: 0 }); }}
          />
        </div>
      )}
    </div>
  );
}
